import { useState } from 'react';
import type { IChecklistItem, ISchedulerTask, SiteId } from '@interfaces/IScheduler';
import { amendTaskChecklist } from '@apis/checklists';
import { checklistDelta, type IChecklistDelta } from '@d-orca/shared/checklist';
import { usePullPlan } from './usePullPlan';

// ---------------------------------------------------------------------------
// Amending a COMMITTED task's checklist (task-checklist design, decision 5):
// an explicit act, drafted apart from the drawer and saved at once through
// its own route — never a quiet edit riding the next Save. An open draft
// carries its `delta`: what the save will record (the same counts the task's
// log and audit will state).
// ---------------------------------------------------------------------------

/** An amendment being drafted — only an open draft can be edited or saved. */
export interface IChecklistAmendmentDraft {
  items: IChecklistItem[];
  delta: IChecklistDelta;
  pending: boolean;
  failed: boolean;
  edit: (items: IChecklistItem[]) => void;
  save: () => Promise<void>;
  cancel: () => void;
}

export interface IChecklistAmendment {
  /** The committed list, in order. */
  committed: IChecklistItem[];
  /** The open draft, or null while the list is just shown. */
  draft: IChecklistAmendmentDraft | null;
  /** Open a draft of the committed list, to be saved to `scheduleNumber`. */
  start: (scheduleNumber: string) => void;
}

/** `onSaved` receives the checklist as the server stored it. */
export function useChecklistAmendment(
  siteId: SiteId,
  task: ISchedulerTask,
  onSaved: (checklist: IChecklistItem[]) => void,
): IChecklistAmendment {
  const pullPlan = usePullPlan(siteId);
  const [open, setOpen] = useState<{ items: IChecklistItem[]; scheduleNumber: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const committed = [...(task.checklist ?? [])].sort((a, b) => a.order - b.order);

  const draftOf = (current: { items: IChecklistItem[]; scheduleNumber: string }): IChecklistAmendmentDraft => ({
    items: current.items,
    delta: checklistDelta(committed, current.items),
    pending,
    failed,
    edit: (items) => setOpen({ ...current, items }),
    cancel: () => setOpen(null),
    save: async () => {
      setPending(true);
      setFailed(false);
      try {
        const { checklist } = await amendTaskChecklist(siteId, current.scheduleNumber, task.id, current.items);
        onSaved(checklist);
        setOpen(null);
        // The amendment moved the schedule's version: rebase onto it.
        await pullPlan(current.scheduleNumber);
      } catch {
        setFailed(true);
      } finally {
        setPending(false);
      }
    },
  });

  return {
    committed,
    draft: open && draftOf(open),
    start: (scheduleNumber) => setOpen({ items: committed, scheduleNumber }),
  };
}
